import { useEffect, useState } from "react";
import { LifeBuoy, X, Send, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PROGERE_MODULE } from "../config/progereModule";

// Bouton universel « Besoin d'aide ? » (Spec §9) — présent dans le topbar, préremplit les diagnostics (§16).
export function SupportTicketButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState({ categories: [], priorities: [] });
  const [form, setForm] = useState({ subject: "", message: "", email: "", phone: "", category: "", priority: "Normale" });

  useEffect(() => { setForm((f) => ({ ...f, email: user?.email || "" })); }, [user]);
  useEffect(() => { if (open && !meta.categories.length) api.supportMeta().then(setMeta).catch(() => {}); }, [open, meta.categories.length]);

  const diagnostics = () => ({
    module: PROGERE_MODULE.code,
    page: window.location.pathname,
    browser: navigator.userAgent,
    language: (navigator.language || "").slice(0, 5),
    online: navigator.onLine ? "en ligne" : "hors ligne",
    screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) { toast.error("Décrivez votre problème."); return; }
    if (!form.email.trim()) { toast.error("Indiquez un courriel pour la réponse."); return; }
    setBusy(true);
    try {
      const d = await api.createSupportTicket({ ...form, page: window.location.pathname, diagnostics: diagnostics() });
      setOpen(false);
      setForm({ subject: "", message: "", email: user?.email || "", phone: "", category: "", priority: "Normale" });
      toast.success(`Billet créé — ${d.ticket_no}`, { description: "Notre équipe technique vous répondra. Un courriel de confirmation vous a été envoyé." });
    } catch (err) {
      toast.error("Échec de l'envoi", { description: err?.response?.data?.detail || "Réessayez plus tard." });
    } finally { setBusy(false); }
  };

  return (
    <>
      <button className="top-btn" onClick={() => setOpen(true)} data-testid="support-ticket-btn" title="Besoin d'aide ? Ouvrir un billet">
        <LifeBuoy size={18} />
      </button>

      {open && (
        <div onClick={() => setOpen(false)} className="support-overlay" data-testid="support-ticket-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="card gold-border" style={{ width: 560, maxWidth: "100%", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, display: "flex", alignItems: "center", gap: 8 }}><LifeBuoy size={18} /> Ouvrir un billet</h3>
              <button type="button" className="top-btn" onClick={() => setOpen(false)}><X size={18} /></button>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <label className="field-label">Catégorie</label>
                <select className="select" style={{ width: "100%" }} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="support-category">
                  <option value="">— Choisir —</option>
                  {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <label className="field-label">Priorité</label>
                <select className="select" style={{ width: "100%" }} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="support-priority">
                  {meta.priorities.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>

            <label className="field-label">Sujet (optionnel)</label>
            <input className="input" style={{ width: "100%" }} placeholder="Ex. Problème de pointage GPS" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="support-tb-subject" />

            <label className="field-label" style={{ marginTop: 12 }}>Décrivez votre problème *</label>
            <textarea className="input" rows={5} style={{ width: "100%", resize: "vertical", fontFamily: "inherit" }} placeholder="Expliquez le problème, l'écran concerné et les étapes pour le reproduire…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="support-tb-message" required />

            <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <label className="field-label">Courriel de réponse *</label>
                <input className="input" style={{ width: "100%" }} type="email" placeholder="vous@entreprise.ca" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="support-tb-email" required />
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <label className="field-label">Téléphone (rappel)</label>
                <input className="input" style={{ width: "100%" }} placeholder="(819) 555-0123" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="support-tb-phone" />
              </div>
            </div>

            <div className="support-diag" data-testid="support-diagnostics">
              <ShieldCheck size={13} /> Informations techniques jointes automatiquement : module <b>PUNCH</b>, page <b>{window.location.pathname}</b>, navigateur, langue, connexion. <span>Aucun mot de passe ni donnée sensible n'est collecté.</span>
            </div>

            <button className="btn btn-gold" type="submit" disabled={busy} style={{ width: "100%", marginTop: 16, justifyContent: "center" }} data-testid="support-tb-submit">
              {busy ? <Loader2 size={16} className="spin" /> : <Send size={16} />} Envoyer le billet
            </button>
          </form>
        </div>
      )}
    </>
  );
}
